import {
    useLayoutEffect,
    type ReactNode,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type ITreeViewProps from "./type";
import type {
    TreeViewWebPropsType,
    TreeViewContextType,
    TreeViewEntryType,
    TreeViewSnapshot
} from "./type";
import stylesheet, {
    getTreeViewMeasures,
    useStyles
} from "./stylesheet";
import TreeViewItem from "./components/item";
import TreeViewPlaceholder from "./components/placeholder";
import {
    createTreeViewStore,
    TreeViewContext
} from "./context";
import useTreeViewState from "./hook";
import {
    TREE_VIEW_ROOT_PLACEHOLDER_COUNT,
    TREE_VIEW_PLACEHOLDER_COUNT,
    getTreeViewAncestorTitles,
    TREE_VIEW_PATH_SEPARATOR,
    TREE_VIEW_NO_NODES,
    TREE_VIEW_NO_KEYS
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    ShieldAlert as ShieldAlertIcon,
    FolderOpen as FolderOpenIcon,
    ArrowLeft as ArrowLeftIcon
} from "lucide-react-native";
import {
    getMirrorStyle,
    isMirroredIcon
} from "../../helpers/direction";
import Skeleton from "../skeleton";
import Button from "../button";
import Alert from "../alert";
import Icon from "../icon";
import Text from "../text";

const TreeViewBackIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => {
    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    return <ArrowLeftIcon
        style={getMirrorStyle({
            isMirrored: isMirroredIcon({
                name: "ArrowLeft"
            }),
            direction
        })}
        color={customColor}
        size={size}
    />;
};

const TreeView: FC<ITreeViewProps> = ({
    defaultExpandedKeys = TREE_VIEW_NO_KEYS,
    defaultSelectedKeys = TREE_VIEW_NO_KEYS,
    expandedKeys: expandedKeysProp,
    selectedKeys: selectedKeysProp,
    checkBehaviour = "cascade",
    isPermissionDenied = false,
    nodes = TREE_VIEW_NO_NODES,
    layoutBehaviour = "auto",
    "aria-label": ariaLabel,
    isGuideVisible = false,
    selectionMode = "none",
    accessibilityLabel,
    maxIndentLevel = 3,
    isLoading = false,
    variant = "plain",
    onRequestAccess,
    size = "medium",
    customLocalize,
    onLoadChildren,
    renderItemMenu,
    onDrillChange,
    emptyContent,
    error = null,
    customTheme,
    onExpand,
    onSelect,
    onPress,
    onRetry,
    style,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const measures = useMemo(() => getTreeViewMeasures({
        controlHeights,
        touchTargets,
        pointerType,
        iconSizes,
        radiuses,
        borders,
        variant,
        spaces,
        size
    }), [
        controlHeights,
        touchTargets,
        pointerType,
        iconSizes,
        radiuses,
        borders,
        variant,
        spaces,
        size
    ]);

    const {
        handleLongPressEntry,
        handleMenuOpenChange,
        handleToggleExpand,
        childrenByParent,
        handlePressEntry,
        handleFocusKey,
        checkedStates,
        handleKeyDown,
        isViewLoading,
        announcement,
        handleRetry,
        rootEntries,
        selectedSet,
        activePath,
        handleBack,
        tabStopKey,
        itemRefs,
        nodeMap,
        menuKey
    } = useTreeViewState({
        expandedKeys: expandedKeysProp,
        selectedKeys: selectedKeysProp,
        locale: activeLocale,
        defaultExpandedKeys,
        defaultSelectedKeys,
        layoutBehaviour,
        checkBehaviour,
        maxIndentLevel,
        onLoadChildren,
        renderItemMenu,
        onDrillChange,
        selectionMode,
        durations,
        localize,
        onExpand,
        onSelect,
        onPress,
        nodes
    });

    const {
        emptyActions: emptyActionsDynamicStyle,
        headerText: headerTextDynamicStyle,
        container: containerDynamicStyle,
        emptyIcon: emptyIconDynamicStyle,
        header: headerDynamicStyle,
        status: statusDynamicStyle,
        empty: emptyDynamicStyle,
        list: listDynamicStyle
    } = useStyles({
        controlHeights,
        shapeConfigs,
        shadowAlpha,
        elevations,
        measures,
        radiuses,
        borders,
        variant,
        colors,
        spaces
    });

    const handlersRef = useRef({
        handleLongPressEntry,
        handleMenuOpenChange,
        handleToggleExpand,
        handlePressEntry,
        handleFocusKey,
        handleRetry
    });

    handlersRef.current = {
        handleLongPressEntry,
        handleMenuOpenChange,
        handleToggleExpand,
        handlePressEntry,
        handleFocusKey,
        handleRetry
    };

    const stableHandlers = useMemo(() => ({
        handleMenuOpenChange: (key: string, isOpen: boolean) => handlersRef.current.handleMenuOpenChange(key, isOpen),
        handleLongPressEntry: (entry: TreeViewEntryType) => handlersRef.current.handleLongPressEntry(entry),
        handleToggleExpand: (entry: TreeViewEntryType) => handlersRef.current.handleToggleExpand(entry),
        handlePressEntry: (entry: TreeViewEntryType) => handlersRef.current.handlePressEntry(entry),
        handleRetry: (entry: TreeViewEntryType) => handlersRef.current.handleRetry(entry),
        handleFocusKey: (key: string) => handlersRef.current.handleFocusKey(key)
    }), []);

    const snapshot = useMemo((): TreeViewSnapshot => ({
        selectedKeys: selectedSet,
        childrenByParent,
        checkedStates,
        tabStopKey,
        menuKey
    }), [
        childrenByParent,
        checkedStates,
        selectedSet,
        tabStopKey,
        menuKey
    ]);

    const [
        store
    ] = useState(() => createTreeViewStore(snapshot));

    store.setSnapshot(snapshot);

    useLayoutEffect(() => {
        store.notify();
    }, [
        snapshot,
        store
    ]);

    const contextValue = useMemo((): TreeViewContextType => ({
        ...stableHandlers,
        customLocalize,
        isGuideVisible,
        renderItemMenu,
        selectionMode,
        customTheme,
        itemRefs,
        measures,
        variant,
        store,
        size
    }), [
        customLocalize,
        stableHandlers,
        isGuideVisible,
        renderItemMenu,
        selectionMode,
        customTheme,
        itemRefs,
        measures,
        variant,
        store,
        size
    ]);

    const currentLabel = ariaLabel ?? accessibilityLabel ?? localize("tree-view-label");

    const drillNode = activePath[activePath.length - 1];

    const webProps: TreeViewWebPropsType = Platform.OS === "web" ? {
        "aria-multiselectable": selectionMode === "multiple" ? true : undefined,
        onKeyDown: handleKeyDown
    } : {};

    const renderStatus = () => {
        return <View
            style={[
                stylesheet.status,
                statusDynamicStyle
            ]}
            aria-live="polite"
            role="status"
        >
            <Text
                customTheme={customTheme}
            >
                {announcement}
            </Text>
        </View>;
    };

    const renderHeader = () => {
        if(!drillNode) {
            return null;
        }

        const ancestorTitles = getTreeViewAncestorTitles({
            key: drillNode.key,
            nodeMap
        });

        const parentTitle = ancestorTitles[ancestorTitles.length - 1];

        return <View
            style={[
                stylesheet.header,
                headerDynamicStyle
            ]}
        >
            <Button
                accessibilityLabel={parentTitle ? localize("go-back-to", [
                    parentTitle
                ]) : localize("go-back")}
                customTheme={customTheme}
                icon={TreeViewBackIcon}
                onPress={handleBack}
                variant="ghost"
                type="neutral"
                size="large"
            />
            <View
                style={[
                    stylesheet.headerText,
                    headerTextDynamicStyle
                ]}
            >
                <Text
                    customTheme={customTheme}
                    variant="titleMediumSize"
                    numberOfLines={1}
                >
                    {drillNode.title}
                </Text>
                {
                    ancestorTitles.length ?
                        <Text
                            customColor={colors.content.text.mid}
                            customTheme={customTheme}
                            variant="bodySmallSize"
                            numberOfLines={1}
                        >
                            {ancestorTitles.join(TREE_VIEW_PATH_SEPARATOR)}
                        </Text>
                    :
                        null
                }
            </View>
        </View>;
    };

    const renderState = ({
        description,
        action,
        title,
        icon
    }: {
        icon: typeof FolderOpenIcon;
        description: string;
        action?: ReactNode;
        title: string;
    }) => {
        return <View
            style={[
                stylesheet.empty,
                emptyDynamicStyle
            ]}
        >
            <View
                style={[
                    stylesheet.emptyIcon,
                    emptyIconDynamicStyle
                ]}
                aria-hidden={true}
            >
                <Icon
                    customTheme={customTheme}
                    size={iconSizes.large}
                    icon={icon}
                />
            </View>
            <Text
                style={stylesheet.emptyText}
                customTheme={customTheme}
                variant="titleMediumSize"
            >
                {title}
            </Text>
            <Text
                customColor={colors.content.text.mid}
                style={stylesheet.emptyText}
                customTheme={customTheme}
                variant="bodyMediumSize"
            >
                {description}
            </Text>
            {
                action ?
                    <View
                        style={emptyActionsDynamicStyle}
                    >
                        {action}
                    </View>
                :
                    null
            }
        </View>;
    };

    const renderEntries = () => {
        if(rootEntries.length) {
            return rootEntries.map((entry) => <TreeViewItem
                key={entry.key}
                entry={entry}
            />);
        }

        if(isViewLoading) {
            return <TreeViewPlaceholder
                count={TREE_VIEW_PLACEHOLDER_COUNT}
                kind="skeleton"
                level={1}
            />;
        }

        return <TreeViewPlaceholder
            kind="note"
            level={1}
        />;
    };

    const renderBody = () => {
        if(isPermissionDenied) {
            return renderState({
                description: localize("tree-view-denied-description"),
                action: onRequestAccess ? <Button
                    title={localize("tree-view-request-access")}
                    onPress={onRequestAccess}
                    customTheme={customTheme}
                    variant="outline"
                    type="neutral"
                    size="small"
                /> : undefined,
                title: localize("tree-view-denied-title"),
                icon: ShieldAlertIcon
            });
        }

        if(isLoading) {
            return <Skeleton.Group
                placeholder={<TreeViewPlaceholder
                    count={TREE_VIEW_ROOT_PLACEHOLDER_COUNT}
                    kind="skeleton"
                    level={1}
                />}
                loadingLabel={localize("loading")}
                customLocalize={customLocalize}
                customTheme={customTheme}
                isLoading={true}
            />;
        }

        if(error) {
            return <Alert
                actions={onRetry ? [
                    {
                        title: localize("try-again"),
                        onPress: onRetry
                    }
                ] : undefined}
                customLocalize={customLocalize}
                customTheme={customTheme}
                type="danger"
                title={error}
            />;
        }

        if(!nodes.length && !drillNode) {
            if(emptyContent) {
                return emptyContent;
            }

            return renderState({
                description: localize("tree-view-empty-description"),
                title: localize("tree-view-empty-title"),
                icon: FolderOpenIcon
            });
        }

        return <View
            {...webProps}
            aria-busy={isViewLoading || undefined}
            aria-label={currentLabel}
            style={[
                stylesheet.list,
                listDynamicStyle
            ]}
            role="tree"
        >
            {renderEntries()}
        </View>;
    };

    return <TreeViewContext.Provider
        value={contextValue}
    >
        <View
            {...props}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
        >
            {renderHeader()}
            {renderBody()}
            {renderStatus()}
        </View>
    </TreeViewContext.Provider>;
};
export default TreeView;
